import { useEffect, useState, useCallback, useMemo } from "react";
import { useApi } from "../context/useApi";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaTimes
} from "react-icons/fa";

const API_BASE = "https://admin-backend-fbuuh6fo3q-el.a.run.app/deals";
const TABS = [
  "Basic Info",
  "Targeting",
  "Benefit",
  "Conditions",
  "Schedule",
  "Add-ons",
];

export default function DealsPanel() {
  const { apiFetch } = useApi();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [banks, setBanks] = useState([]);
  const [cards, setCards] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, deal: null });

  const [toast, setToast] = useState({ message: "", type: "" });
  const [activeTab, setActiveTab] = useState(0);
  const [isLoadingTargets, setIsLoadingTargets] = useState(false);

  const initialDealFormV2 = {
    title: "",
    description: "",
    type: "discount",
    priority: 100,
    status: "active",
    applies_to: {
      store_channels: ["online", "offline"],
      regions: ["IN"],
      context: {
        vendor_ids: [],
        store_ids: [],
        brand_ids: [],
        product_ids: [],
        variant_ids: [],
        category_ids: [],
      },
      excludes: {
        product_ids: [],
        variant_ids: [],
        category_ids: [],
      },
    },

    addons: [],

    benefit: {
      kind: "discount",
      calc: {
        basis: "basket",
        value_type: "percent",
        value: 0,
        currency: null,
        max: null,
        apply_on: "post_tax",
        rounding: "nearest",
      },
      cashback_mode: "instant",
    },
    conditions: {
      min_purchase: 0,
      max_purchase: null,
      first_purchase: { required: false, scope: null },
      code: { type: "none", value: null, auto_apply: true },
      limit: { per_user: null, per_card: null, global: null, reset: "none" },
    },
    schedule: {
      start: "",
      end: "",
      timezone: "Asia/Kolkata",
      valid_days: [0, 1, 2, 3, 4, 5, 6],
      time_windows: [],
    },
    stacking: {
      group: "merchant_offer",
      combinability: "STACK",
    },
  };
  const [dealFormV2, setDealFormV2] = useState(initialDealFormV2);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 3000);
  };

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/get-deals`);
      if (!res) return;
      const data = await res.json();
      if (res.ok && data.success) {
        setDeals(data.deals || []);
      } else {
        showToast(data.error || "Failed to fetch deals", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Error fetching deals", "error");
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  useEffect(() => {
    if (!isModalOpen) return;

    const fetchAllTargets = async () => {
      setIsLoadingTargets(true);
      try {
        const [catRes, vendorRes, brandRes, bankRes] = await Promise.all([
          apiFetch(
            "https://admin-backend-fbuuh6fo3q-el.a.run.app/productcategory/get-categories"
          ),
          apiFetch(
            "https://admin-backend-fbuuh6fo3q-el.a.run.app/vendors/vendors/stores"
          ),
          apiFetch(
            "https://admin-backend-fbuuh6fo3q-el.a.run.app/brands/brands/products"
          ),
          apiFetch(
            "https://admin-backend-fbuuh6fo3q-el.a.run.app/banks/banks/cards"
          ),
        ]);

        // Process Categories
        if (catRes && catRes.ok) {
          const catData = await catRes.json();
          if (catData.success) {
            setCategories(catData.data || []);
          }
        }

        // Process Vendors and Stores
        if (vendorRes && vendorRes.ok) {
          const vendorData = await vendorRes.json();
          setVendors(vendorData.vendors || []);
          const allStores = vendorData.vendors.flatMap((v) => v.stores || []);
          setStores(allStores);
        }

        // Process Brands and Products
        if (brandRes && brandRes.ok) {
          const brandData = await brandRes.json();
          if (brandData.success) {
            setBrands(brandData.brands || []);
            const allProducts = brandData.brands.flatMap(
              (b) => b.products || []
            );
            setProducts(allProducts);
          }
        }

        // Process Banks and Cards
        if (bankRes && bankRes.ok) {
          const bankData = await bankRes.json();
          setBanks(bankData.banks || []);
          const allCards = bankData.banks.flatMap((b) =>
            (b.cards || []).map((c) => ({
              ...c,
              display_name: `${c.name} (${b.bank_name || b.name})`,
            }))
          );
          setCards(allCards);
        }
      } catch (e) {
        console.error("Error fetching targets:", e);
        showToast("Error loading data for form", "error");
      } finally {
        setIsLoadingTargets(false);
      }
    };

    fetchAllTargets();
  }, [isModalOpen, apiFetch]);
  
  const handleOpenModal = (deal = null) => {
    if (deal) {
      setEditingDeal(deal.deal_id);

      if (deal.schema_version === 2) {
        setDealFormV2(deal);
      } else {
        const convertedDeal = {
          ...initialDealFormV2,
          deal_id: deal.deal_id,
          title: deal.deal_name || "",
          description: deal.description || "",
          type: mapBenefitTypeToNewType(deal.benefit_type),
          priority: deal.priority || 100,
          status: deal.is_active ? "active" : "paused",
          applies_to: {
            ...initialDealFormV2.applies_to,
            context: {
              ...initialDealFormV2.applies_to.context,
              ...(deal.deal_type === "vendor" && {
                vendor_ids: deal.target_ids || [],
              }),
              ...(deal.deal_type === "category" && {
                category_ids: deal.target_ids || [],
              }),
              ...(deal.deal_type === "product" && {
                product_ids: deal.target_ids || [],
              }),
              ...(deal.deal_type === "store" && {
                store_ids: deal.target_ids || [],
              }),
              ...(deal.deal_type === "brand" && {
                brand_ids: deal.target_ids || [],
              }),
            },
          },
          eligible_cards:
            deal.eligible_cards || initialDealFormV2.eligible_cards,
          benefit: {
            kind: mapBenefitTypeToKind(deal.benefit_type),
            calc: {
              basis: "basket",
              value_type:
                deal.benefit_type === "percentage"
                  ? "percent"
                  : deal.benefit_type === "flat"
                  ? "flat"
                  : "percent",
              value:
                deal.benefit_type === "percentage"
                  ? deal.discount_percent || 0
                  : deal.benefit_type === "flat"
                  ? deal.max_cap || 0
                  : 0,
              currency: deal.benefit_type === "flat" ? "INR" : null,
              max:
                deal.benefit_type === "percentage"
                  ? deal.max_cap || null
                  : null,
              apply_on: "post_tax",
              rounding: "nearest",
            },
            cashback_mode: "instant",
          },
          conditions: {
            ...initialDealFormV2.conditions,
            min_purchase: deal.min_purchase_value || 0,
            max_purchase: deal.max_purchase_value || null,
            code: deal.promo_code
              ? {
                  type: "static",
                  value: deal.promo_code,
                  auto_apply: false,
                }
              : initialDealFormV2.conditions.code,
            limit: {
              per_user: deal.max_uses_per_user || null,
              per_card: deal.per_card_limit || null,
              global: deal.global_limit || null,
              reset: "none",
            },
          },
          schedule: {
            start: deal.start_time || "",
            end: deal.end_time || "",
            timezone: deal.timezone || "Asia/Kolkata",
            valid_days: deal.valid_days || [0, 1, 2, 3, 4, 5, 6],
            time_windows: deal.valid_times
              ? deal.valid_times.map((t) => ({ start: t[0], end: t[1] }))
              : [],
          },
          stacking: deal.stacking || initialDealFormV2.stacking,
        };
        setDealFormV2(convertedDeal);
      }
      setActiveTab(0);
    } else {
      setEditingDeal(null);
      setDealFormV2({ ...initialDealFormV2 });
      setActiveTab(0);
    }
    setIsModalOpen(true);
  };

  const mapBenefitTypeToNewType = (benefitType) => {
    switch (benefitType) {
      case "percentage":
        return "discount";
      case "flat":
        return "discount";
      case "bogo":
        return "promotion";
      case "cashback":
        return "cashback";
      default:
        return "discount";
    }
  };

  const mapBenefitTypeToKind = (benefitType) => {
    switch (benefitType) {
      case "percentage":
        return "discount";
      case "flat":
        return "discount";
      case "bogo":
        return "promotion";
      case "cashback":
        return "cashback";
      default:
        return "discount";
    }
  };

const handleSaveDeal = async (e) => {
  e.preventDefault();
  setIsSaving(true);

  try {
    // --- Main Deal Validation ---
    if (!dealFormV2.title || dealFormV2.title.trim() === "") {
      throw new Error("Deal title is required");
    }
    if (!dealFormV2.schedule.start || !dealFormV2.schedule.end) {
      throw new Error("Start and end dates are required");
    }
    if (dealFormV2.benefit.calc.value <= 0) {
      throw new Error("Main benefit value must be greater than 0");
    }

    // --- Targeting Validation ---
    const hasTargeting =
      dealFormV2.applies_to.context.vendor_ids.length > 0 ||
      dealFormV2.applies_to.context.store_ids.length > 0 ||
      dealFormV2.applies_to.context.brand_ids.length > 0 ||
      dealFormV2.applies_to.context.product_ids.length > 0 ||
      dealFormV2.applies_to.context.category_ids.length > 0;

    // A deal is valid if it has merchant targeting OR if it has a valid add-on.
    // If it has neither, it's an empty, invalid deal.
    const hasAddons = dealFormV2.addons && dealFormV2.addons.length > 0;

    if (!hasTargeting && !hasAddons) {
      throw new Error(
        "A deal must have at least one target (store, brand, etc.) or a card add-on."
      );
    }

    // --- Add-on Validation (if they exist) ---
    if (hasAddons) {
      for (const addon of dealFormV2.addons) {
        if (!addon.title || addon.title.trim() === "") {
          throw new Error("Add-on title is required.");
        }
        if (addon.benefit.calc.value <= 0) {
          throw new Error("Add-on benefit value must be greater than 0.");
        }
        if (
          !addon.eligible_cards.bank_ids ||
          addon.eligible_cards.bank_ids.length === 0
        ) {
          throw new Error(
            "An add-on deal must have at least one bank selected."
          );
        }
      }
    }

    // --- Date and Payload Preparation ---
    const startDate = new Date(dealFormV2.schedule.start);
    const endDate = new Date(dealFormV2.schedule.end);

    if (startDate >= endDate) {
      throw new Error("End date must be after start date");
    }

    // Prepare payload
    const payload = {
      ...dealFormV2,
      schema_version: 2,
      schedule: {
        ...dealFormV2.schedule,
        start: startDate.toISOString(),
        end: endDate.toISOString(),
      },
      // Ensure numeric values are properly typed for the main benefit
      benefit: {
        ...dealFormV2.benefit,
        calc: {
          ...dealFormV2.benefit.calc,
          value: Number(dealFormV2.benefit.calc.value),
          max: dealFormV2.benefit.calc.max
            ? Number(dealFormV2.benefit.calc.max)
            : null,
          currency:
            dealFormV2.benefit.calc.value_type === "flat"
              ? dealFormV2.benefit.calc.currency || "INR"
              : null,
        },
      },
      // Ensure numeric values are properly typed for addons
      addons: (dealFormV2.addons || []).map((addon) => ({
        ...addon,
        benefit: {
          ...addon.benefit,
          calc: {
            ...addon.benefit.calc,
            value: Number(addon.benefit.calc.value),
            max: addon.benefit.calc.max ? Number(addon.benefit.calc.max) : null,
            currency:
              addon.benefit.calc.value_type === "flat"
                ? addon.benefit.calc.currency || "INR"
                : null,
          },
        },
      })),
      updated_at: new Date().toISOString(),
      ...(!editingDeal && { created_at: new Date().toISOString() }),
    };


    // --- API Call ---
    const url = editingDeal
      ? `${API_BASE}/update-deal/${editingDeal}`
      : `${API_BASE}/add-deal`;

    const res = await apiFetch(url, {
      method: editingDeal ? "PUT" : "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!res) {
      throw new Error("No response from server");
    }

    const data = await res.json();

    if (res.ok) {
      showToast(
        editingDeal
          ? "Deal updated successfully!"
          : "Deal created successfully!",
        "success"
      );
      setIsModalOpen(false);
      setEditingDeal(null);
      setDealFormV2(initialDealFormV2);
      setActiveTab(0);
      fetchDeals();
    } else {
      throw new Error(data.error || data.message || "Failed to save deal");
    }
  } catch (err) {
    console.error("Error saving deal:", err);
    showToast(err.message, "error");
  } finally {
    setIsSaving(false);
  }
};

  const openDeleteModal = (deal) => {
    setDeleteModal({ isOpen: true, deal });
  };

  const handleDelete = async () => {
    const deal = deleteModal.deal;
    if (!deal) return;

    setIsSaving(true); // Reuse the saving state to disable buttons

    try {
      const res = await apiFetch(`${API_BASE}/delete-deal/${deal.deal_id}`, {
        method: "DELETE",
      });

      if (!res) return;
      const data = await res.json();

      if (res.ok) {
        showToast(data.message || "Deal deleted successfully", "success");
        setDeleteModal({ isOpen: false, deal: null });
        fetchDeals();
      } else {
        showToast(data.error || "Failed to delete deal", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Error deleting deal", "error");
    } finally {
      setIsSaving(false);
    }
  };
  const getStatusBadge = (deal) => {
    const status = deal.computed_status || deal.status;
    const statusConfig = {
      active: {
        bg: "bg-green-100",
        text: "text-green-800",
        dot: "bg-green-400",
      },
      scheduled: {
        bg: "bg-blue-100",
        text: "text-blue-800",
        dot: "bg-blue-400",
      },
      paused: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        dot: "bg-yellow-400",
      },
      expired: { bg: "bg-red-100", text: "text-red-800", dot: "bg-red-400" },
      draft: { bg: "bg-gray-100", text: "text-gray-800", dot: "bg-gray-400" },
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        <span className={`w-2 h-2 mr-1.5 rounded-full ${config.dot}`}></span>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getBenefitDisplay = (deal) => {
    if (!deal?.benefit?.calc) {
      return "-";
    }

    const { calc, kind } = deal.benefit;
    const addons = deal.addons || [];

    const kindLabel =
      kind === "cashback" ? "Cashback" : kind === "discount" ? "Off" : kind;
    let baseBenefitText = "";

    if (calc.value_type === "percent") {
      baseBenefitText = `${calc.value}% ${kindLabel}${
        calc.max ? ` (up to ₹${calc.max})` : ""
      }`;
    } else if (calc.value_type === "flat") {
      baseBenefitText = `₹${calc.value} ${kindLabel}`;
    } else {
      baseBenefitText = `${calc.value} ${calc.value_type || "Benefit"}`;
    }

    const hasAddon = addons.length > 0;

    return (
      <div>
        <div className="font-semibold text-gray-900">{baseBenefitText}</div>
        {hasAddon && (
          <div className="mt-1">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
              + {addons.length} Card Add-on{addons.length > 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>
    );
  };

  const getTargetsDisplay = (deal) => {
    if (!deal?.applies_to?.context) {
      return "Global";
    }

    const { context } = deal.applies_to;

    const targets = [];
    if (context.store_ids?.length) {
      targets.push(
        `${context.store_ids.length} Store${
          context.store_ids.length > 1 ? "s" : ""
        }`
      );
    }
    if (context.vendor_ids?.length) {
      targets.push(
        `${context.vendor_ids.length} Vendor${
          context.vendor_ids.length > 1 ? "s" : ""
        }`
      );
    }
    if (context.brand_ids?.length) {
      targets.push(
        `${context.brand_ids.length} Brand${
          context.brand_ids.length > 1 ? "s" : ""
        }`
      );
    }
    if (context.category_ids?.length) {
      targets.push(
        `${context.category_ids.length} Categor${
          context.category_ids.length > 1 ? "ies" : "y"
        }`
      );
    }
    if (context.product_ids?.length) {
      targets.push(
        `${context.product_ids.length} Product${
          context.product_ids.length > 1 ? "s" : ""
        }`
      );
    }

    if (targets.length === 0) {
      // If context is empty, it's a global deal.
      // It might be an independent bank offer without any merchant target.
      return <span className="text-gray-500">Global / Card Offer</span>;
    }

    return targets.join(" & ");
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
    });
  };

  const getValidityDisplay = (deal) => {
    const start = deal.schedule?.start || deal.start_time;
    const end = deal.schedule?.end || deal.end_time;

    return (
      <div className="text-xs">
        <div>{formatDateShort(start)}</div>
        <div className="text-gray-400">to</div>
        <div>{formatDateShort(end)}</div>
      </div>
    );
  };

  const getTypeChip = (deal) => {
    const type = deal.type || deal.deal_type || "unknown";
    const typeColors = {
      discount: "bg-blue-50 text-blue-700 border-blue-200",
      cashback: "bg-green-50 text-green-700 border-green-200",
      points: "bg-purple-50 text-purple-700 border-purple-200",
      promotion: "bg-orange-50 text-orange-700 border-orange-200",
      product: "bg-gray-50 text-gray-700 border-gray-200",
      vendor: "bg-indigo-50 text-indigo-700 border-indigo-200",
      category: "bg-pink-50 text-pink-700 border-pink-200",
    };

    const colorClass =
      typeColors[type] || "bg-gray-50 text-gray-700 border-gray-200";
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${colorClass}`}
      >
        {type}
      </span>
    );
  };
  const filteredDeals = deals.filter((deal) => {
    const status =
      deal.computed_status || (deal.is_active ? "active" : "inactive");
    const matchesStatus = filterStatus === "all" || status === filterStatus;

    const title = (deal.title || deal.deal_name || "").toLowerCase();
    const desc = (deal.description || "").toLowerCase();
    const matchesSearch =
      !searchTerm ||
      title.includes(searchTerm.toLowerCase()) ||
      desc.includes(searchTerm.toLowerCase());

    return matchesStatus && matchesSearch;
  });
  const AddonDealForm = ({
    addon,
    index,
    dealFormV2,
    setDealFormV2,
    banks,
    cards,
    isLoadingTargets,
  }) => {
    const filteredCardsForThisAddon = useMemo(() => {
      if (!cards?.length || !addon.eligible_cards?.bank_ids?.length) {
        return [];
      }
      return cards.filter((card) =>
        addon.eligible_cards.bank_ids.includes(card.bank_id)
      );
    }, [cards, addon.eligible_cards?.bank_ids]);

    const updateAddon = (updatedFields) => {
      const newAddons = [...dealFormV2.addons];
      newAddons[index] = { ...newAddons[index], ...updatedFields };
      setDealFormV2({ ...dealFormV2, addons: newAddons });
    };

    const handleBankSelection = (e) => {
      const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
      updateAddon({
        eligible_cards: {
          ...addon.eligible_cards,
          bank_ids: selected,
          card_ids: [], // Reset card selection when banks change
        },
      });
    };

    const handleCardSelection = (e) => {
      const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
      updateAddon({
        eligible_cards: {
          ...addon.eligible_cards,
          card_ids: selected,
        },
      });
    };

    return (
      <div className="space-y-4">
        {/* Title */}
        <input
          type="text"
          placeholder="Add-on Title (e.g., HDFC 3% Cashback)"
          className="w-full border rounded-lg p-2 text-sm"
          value={addon.title}
          onChange={(e) => updateAddon({ title: e.target.value })}
        />

        {/* Benefit */}
        <div className="grid grid-cols-3 gap-4">
          <select
            className="w-full border rounded-lg p-2 text-sm"
            value={addon.benefit.calc.value_type}
            onChange={(e) =>
              updateAddon({
                benefit: {
                  ...addon.benefit,
                  calc: { ...addon.benefit.calc, value_type: e.target.value },
                },
              })
            }
          >
            <option value="percent">Percentage</option>
            <option value="flat">Flat</option>
          </select>
          <input
            type="number"
            placeholder="Value"
            className="w-full border rounded-lg p-2 text-sm"
            value={addon.benefit.calc.value}
            onChange={(e) =>
              updateAddon({
                benefit: {
                  ...addon.benefit,
                  calc: {
                    ...addon.benefit.calc,
                    value: parseFloat(e.target.value) || 0,
                  },
                },
              })
            }
          />
          <input
            type="number"
            placeholder="Max Cap"
            className="w-full border rounded-lg p-2 text-sm"
            value={addon.benefit.calc.max || ""}
            onChange={(e) =>
              updateAddon({
                benefit: {
                  ...addon.benefit,
                  calc: {
                    ...addon.benefit.calc,
                    max: parseInt(e.target.value) || null,
                  },
                },
              })
            }
          />
        </div>

        {/* Card Eligibility */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t">
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">
              Banks *
            </label>
            <select
              multiple
              required
              disabled={isLoadingTargets}
              className="w-full border rounded-lg p-2 h-24 text-sm"
              value={addon.eligible_cards.bank_ids}
              onChange={handleBankSelection}
            >
              {banks.map((b) => (
                <option key={b.bank_id} value={b.bank_id}>
                  {b.bank_name || b.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">
              Specific Cards (Optional)
            </label>
            <select
              multiple
              disabled={
                isLoadingTargets || !addon.eligible_cards.bank_ids?.length
              }
              className="w-full border rounded-lg p-2 h-24 text-sm disabled:bg-gray-100"
              value={addon.eligible_cards.card_ids}
              onChange={handleCardSelection}
            >
              {filteredCardsForThisAddon.map((c) => (
                <option key={c.card_id} value={c.card_id}>
                  {c.display_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  };
  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-10">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Deals</h2>
          <p className="text-sm text-gray-500">
            Manage all active and upcoming deals for your platform.
          </p>
        </div>

        <button
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 hover:text-blue-700 transition-colors"
        >
          <FaPlus />
          Add Deal
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by title or description..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Status Filter */}
          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="scheduled">Scheduled</option>
            <option value="paused">Paused</option>
            <option value="expired">Expired</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
  <div className="space-y-4">
    {[1, 2, 3].map((i) => (
      <div key={i} className="border-2 border-gray-200 rounded-lg p-6 animate-pulse">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-5 border-r-2 border-gray-200 pr-6">
            <div className="flex items-start justify-between mb-3">
              <div className="h-6 w-20 border border-gray-300 rounded-full"></div>
              <div className="flex gap-2">
                <div className="h-6 w-6 border border-gray-300 rounded"></div>
                <div className="h-6 w-6 border border-gray-300 rounded"></div>
              </div>
            </div>
            <div className="h-6 w-3/4 border border-gray-300 rounded mb-2"></div>
            <div className="h-4 w-full border border-gray-300 rounded mb-1"></div>
            <div className="h-4 w-2/3 border border-gray-300 rounded"></div>
          </div>
          <div className="col-span-4 border-r-2 border-gray-200 pr-6">
            <div className="h-10 w-24 border border-gray-300 rounded mb-2"></div>
            <div className="h-4 w-32 border border-gray-300 rounded"></div>
          </div>
          <div className="col-span-3">
            <div className="h-4 w-full border border-gray-300 rounded mb-2"></div>
            <div className="h-4 w-3/4 border border-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
) : filteredDeals.length === 0 ? (
  <div className="border-2 border-dashed border-gray-300 rounded-lg p-16 text-center">
    <div className="inline-block p-4 border-2 border-gray-300 rounded-full mb-4">
      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    </div>
    <p className="text-gray-600 font-medium mb-1">No deals found</p>
    <p className="text-sm text-gray-500">Try adjusting your filters or search criteria</p>
  </div>
) : (
        <div className="space-y-4">
          {filteredDeals.map((deal) => (
            <div
              key={deal.deal_id}
              className="border-2 border-gray-200 rounded-lg p-6 hover:border-gray-400 transition-all"
            >
              <div className="grid grid-cols-12 gap-6">
                {/* Left Section: Deal Info & Status */}
                <div className="col-span-5 border-r-2 border-gray-200 pr-6">
                  <div className="flex items-start justify-between mb-3">
                    {getStatusBadge(deal)}
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-gray-400">
                        P{deal.priority || 0}
                      </span>
                      <button
                        onClick={() => handleOpenModal(deal)}
                        className="p-1.5 text-blue-600 hover:text-blue-800 transition-colors"
                        title="Edit"
                      >
                        <FaEdit size={16} />
                      </button>
                      <button
                        onClick={() => openDeleteModal(deal)}
                        className="p-1.5 text-red-600 hover:text-red-800 transition-colors"
                        title="Delete"
                      >
                        <FaTrash size={16} />
                      </button>
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-2 leading-tight">
                    {deal.title || deal.deal_name}
                  </h3>

                  {deal.description && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {deal.description}
                    </p>
                  )}

                  <div className="flex items-center gap-2 text-xs">
                    {getTypeChip(deal)}
                  </div>
                </div>

                {/* Middle Section: Benefit Details */}
                <div className="col-span-4 border-r-2 border-gray-200 pr-6">
                  <div className="mb-4">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
                      Primary Benefit
                    </label>
                    {getBenefitDisplay(deal)}
                  </div>

                  {deal.addons && deal.addons.length > 0 && (
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
                        Card Add-ons ({deal.addons.length})
                      </label>
                      <div className="space-y-2">
                        {deal.addons.slice(0, 2).map((addon, idx) => (
                          <div
                            key={idx}
                            className="border border-gray-200 rounded p-2"
                          >
                            <div className="text-xs font-medium text-gray-900 mb-1">
                              {addon.title}
                            </div>
                            <div className="text-xs text-gray-600">
                              {addon.benefit?.calc?.value_type === "percent"
                                ? `${addon.benefit.calc.value}% ${addon.benefit.kind}`
                                : `₹${addon.benefit?.calc?.value} ${addon.benefit?.kind}`}
                              {addon.benefit?.calc?.max &&
                                ` (up to ₹${addon.benefit.calc.max})`}
                            </div>
                          </div>
                        ))}
                        {deal.addons.length > 2 && (
                          <div className="text-xs text-gray-500 italic">
                            +{deal.addons.length - 2} more add-ons
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {deal.conditions?.min_purchase > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <span className="text-xs text-gray-500">
                        Min Purchase:{" "}
                      </span>
                      <span className="text-xs font-semibold text-gray-900">
                        ₹{deal.conditions.min_purchase.toLocaleString("en-IN")}
                      </span>
                    </div>
                  )}
                </div>

                {/* Right Section: Targeting & Schedule */}
                <div className="col-span-3">
                  <div className="mb-4">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
                      Applies To
                    </label>
                    <div className="text-sm text-gray-900">
                      {getTargetsDisplay(deal)}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
                      Validity Period
                    </label>
                    {getValidityDisplay(deal)}
                  </div>

                  {deal.stacking && (
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
                        Stacking
                      </label>
                      <div className="text-xs text-gray-900">
                        <div>
                          Group:{" "}
                          <span className="font-mono">
                            {deal.stacking.group}
                          </span>
                        </div>
                        <div>
                          Mode:{" "}
                          <span className="font-mono">
                            {deal.stacking.combinability}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="relative bg-white p-6 rounded-lg shadow-lg w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">
                {editingDeal ? "Edit Deal" : "Create New Deal"}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 transition"
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b mb-6">
              {TABS.map((tab, idx) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(idx)}
                  className={`px-4 py-2 font-medium transition ${
                    activeTab === idx
                      ? "border-b-2 border-blue-600 text-blue-600"
                      : "text-gray-600 hover:text-gray-800"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <form onSubmit={handleSaveDeal} className="flex-1 overflow-y-auto">
              <div className="min-h-[400px]">
                {/* Tab 0: Basic Info */}
                {activeTab === 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Deal Title *
                      </label>
                      <input
                        type="text"
                        className="w-full border rounded-lg p-2"
                        placeholder="10% Cashback on Apple Products"
                        value={dealFormV2.title}
                        onChange={(e) =>
                          setDealFormV2({
                            ...dealFormV2,
                            title: e.target.value,
                          })
                        }
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Deal Type *
                      </label>
                      <select
                        className="w-full border rounded-lg p-2"
                        value={dealFormV2.type}
                        onChange={(e) =>
                          setDealFormV2({ ...dealFormV2, type: e.target.value })
                        }
                      >
                        <option value="discount">Discount</option>
                        <option value="cashback">Cashback</option>
                        <option value="points">Points</option>
                        <option value="accelerated_reward">
                          Accelerated Reward
                        </option>
                        <option value="promotion">Promotion</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Priority
                      </label>
                      <input
                        type="number"
                        className="w-full border rounded-lg p-2"
                        value={dealFormV2.priority}
                        onChange={(e) =>
                          setDealFormV2({
                            ...dealFormV2,
                            priority: parseInt(e.target.value) || 0,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Status
                      </label>
                      <select
                        className="w-full border rounded-lg p-2"
                        value={dealFormV2.status}
                        onChange={(e) =>
                          setDealFormV2({
                            ...dealFormV2,
                            status: e.target.value,
                          })
                        }
                      >
                        <option value="draft">Draft</option>
                        <option value="active">Active</option>
                        <option value="paused">Paused</option>
                        <option value="scheduled">Scheduled</option>
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">
                        Description
                      </label>
                      <textarea
                        className="w-full border rounded-lg p-2"
                        rows={3}
                        placeholder="Describe the deal..."
                        value={dealFormV2.description}
                        onChange={(e) =>
                          setDealFormV2({
                            ...dealFormV2,
                            description: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">
                        Store Channels
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={dealFormV2.applies_to.store_channels.includes(
                              "online"
                            )}
                            onChange={(e) => {
                              const channels = e.target.checked
                                ? [
                                    ...dealFormV2.applies_to.store_channels,
                                    "online",
                                  ]
                                : dealFormV2.applies_to.store_channels.filter(
                                    (c) => c !== "online"
                                  );
                              setDealFormV2({
                                ...dealFormV2,
                                applies_to: {
                                  ...dealFormV2.applies_to,
                                  store_channels: channels,
                                },
                              });
                            }}
                          />
                          Online
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={dealFormV2.applies_to.store_channels.includes(
                              "offline"
                            )}
                            onChange={(e) => {
                              const channels = e.target.checked
                                ? [
                                    ...dealFormV2.applies_to.store_channels,
                                    "online",
                                  ]
                                : dealFormV2.applies_to.store_channels.filter(
                                    (c) => c !== "offline"
                                  );
                              setDealFormV2({
                                ...dealFormV2,
                                applies_to: {
                                  ...dealFormV2.applies_to,
                                  store_channels: channels,
                                },
                              });
                            }}
                          />
                          Offline
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tab 1: Targeting */}
                {activeTab === 1 && (
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">
                        Where does this deal apply?
                      </h4>
                      <p className="text-sm text-gray-500 mb-4">
                        Select multiple targets. They work with AND logic across
                        different types, OR within same type.
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Vendors */}
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Vendors
                          </label>
                          <select
                            multiple
                            className="w-full border rounded-lg p-2 h-32"
                            value={dealFormV2.applies_to.context.vendor_ids}
                            onChange={(e) => {
                              const selected = Array.from(
                                e.target.selectedOptions
                              ).map((o) => o.value);
                              setDealFormV2({
                                ...dealFormV2,
                                applies_to: {
                                  ...dealFormV2.applies_to,
                                  context: {
                                    ...dealFormV2.applies_to.context,
                                    vendor_ids: selected,
                                  },
                                },
                              });
                            }}
                          >
                            {vendors.map((v) => (
                              <option key={v.vendor_id} value={v.vendor_id}>
                                {v.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Brands
                          </label>
                          <select
                            multiple
                            disabled={isLoadingTargets}
                            className="w-full border rounded-lg p-2 h-32 disabled:bg-gray-100"
                            value={dealFormV2.applies_to.context.brand_ids}
                            onChange={(e) => {
                              const selected = Array.from(
                                e.target.selectedOptions
                              ).map((o) => o.value);
                              setDealFormV2({
                                ...dealFormV2,
                                applies_to: {
                                  ...dealFormV2.applies_to,
                                  context: {
                                    ...dealFormV2.applies_to.context,
                                    brand_ids: selected,
                                  },
                                },
                              });
                            }}
                          >
                            {brands.map((b) => (
                              <option key={b.brand_id} value={b.brand_id}>
                                {b.brand_name || b.name || "Unnamed Brand"}
                              </option>
                            ))}
                          </select>
                        </div>
                        {/* Stores */}
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Stores
                          </label>
                          <select
                            multiple
                            className="w-full border rounded-lg p-2 h-32"
                            value={dealFormV2.applies_to.context.store_ids}
                            onChange={(e) => {
                              const selected = Array.from(
                                e.target.selectedOptions
                              ).map((o) => o.value);
                              setDealFormV2({
                                ...dealFormV2,
                                applies_to: {
                                  ...dealFormV2.applies_to,
                                  context: {
                                    ...dealFormV2.applies_to.context,
                                    store_ids: selected,
                                  },
                                },
                              });
                            }}
                          >
                            {stores.map((s) => (
                              <option key={s.store_id} value={s.store_id}>
                                {s.store_name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Categories */}
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Categories
                          </label>
                          <select
                            multiple
                            className="w-full border rounded-lg p-2 h-32"
                            value={dealFormV2.applies_to.context.category_ids}
                            onChange={(e) => {
                              const selected = Array.from(
                                e.target.selectedOptions
                              ).map((o) => o.value);
                              setDealFormV2({
                                ...dealFormV2,
                                applies_to: {
                                  ...dealFormV2.applies_to,
                                  context: {
                                    ...dealFormV2.applies_to.context,
                                    category_ids: selected,
                                  },
                                },
                              });
                            }}
                          >
                            {categories.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.category_name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Products */}
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Products
                          </label>
                          <select
                            multiple
                            className="w-full border rounded-lg p-2 h-32"
                            value={dealFormV2.applies_to.context.product_ids}
                            onChange={(e) => {
                              const selected = Array.from(
                                e.target.selectedOptions
                              ).map((o) => o.value);
                              setDealFormV2({
                                ...dealFormV2,
                                applies_to: {
                                  ...dealFormV2.applies_to,
                                  context: {
                                    ...dealFormV2.applies_to.context,
                                    product_ids: selected,
                                  },
                                },
                              });
                            }}
                          >
                            {products.map((p) => (
                              <option key={p.product_id} value={p.product_id}>
                                {p.product_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tab 2: Benefits */}
                {activeTab === 2 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Benefit Kind *
                      </label>
                      <select
                        className="w-full border rounded-lg p-2"
                        value={dealFormV2.benefit.kind}
                        onChange={(e) =>
                          setDealFormV2({
                            ...dealFormV2,
                            benefit: {
                              ...dealFormV2.benefit,
                              kind: e.target.value,
                            },
                          })
                        }
                      >
                        <option value="discount">Discount</option>
                        <option value="cashback">Cashback</option>
                        <option value="points">Points</option>
                        <option value="multiplier">Multiplier</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Value Type *
                      </label>
                      <select
                        className="w-full border rounded-lg p-2"
                        value={dealFormV2.benefit.calc.value_type}
                        onChange={(e) =>
                          setDealFormV2({
                            ...dealFormV2,
                            benefit: {
                              ...dealFormV2.benefit,
                              calc: {
                                ...dealFormV2.benefit.calc,
                                value_type: e.target.value,
                              },
                            },
                          })
                        }
                      >
                        <option value="percent">Percentage</option>
                        <option value="flat">Flat Amount</option>
                        <option value="points">Points</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Value *
                      </label>
                      <input
                        type="number"
                        className="w-full border rounded-lg p-2"
                        placeholder="e.g., 10"
                        value={dealFormV2.benefit.calc.value}
                        onChange={(e) =>
                          setDealFormV2({
                            ...dealFormV2,
                            benefit: {
                              ...dealFormV2.benefit,
                              calc: {
                                ...dealFormV2.benefit.calc,
                                value: parseFloat(e.target.value) || 0,
                              },
                            },
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Max Cap (₹)
                      </label>
                      <input
                        type="number"
                        className="w-full border rounded-lg p-2"
                        placeholder="e.g., 1000"
                        value={dealFormV2.benefit.calc.max || ""}
                        onChange={(e) =>
                          setDealFormV2({
                            ...dealFormV2,
                            benefit: {
                              ...dealFormV2.benefit,
                              calc: {
                                ...dealFormV2.benefit.calc,
                                max: parseInt(e.target.value) || null,
                              },
                            },
                          })
                        }
                      />
                    </div>

                    {dealFormV2.benefit.calc.value_type === "flat" && (
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Currency *
                        </label>
                        <select
                          className="w-full border rounded-lg p-2"
                          value={dealFormV2.benefit.calc.currency || "INR"}
                          onChange={(e) =>
                            setDealFormV2({
                              ...dealFormV2,
                              benefit: {
                                ...dealFormV2.benefit,
                                calc: {
                                  ...dealFormV2.benefit.calc,
                                  currency: e.target.value,
                                },
                              },
                            })
                          }
                        >
                          <option value="INR">INR</option>
                          <option value="USD">USD</option>
                        </select>
                      </div>
                    )}

                    {dealFormV2.benefit.kind === "cashback" && (
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Cashback Mode
                        </label>
                        <select
                          className="w-full border rounded-lg p-2"
                          value={dealFormV2.benefit.cashback_mode}
                          onChange={(e) =>
                            setDealFormV2({
                              ...dealFormV2,
                              benefit: {
                                ...dealFormV2.benefit,
                                cashback_mode: e.target.value,
                              },
                            })
                          }
                        >
                          <option value="instant">Instant</option>
                          <option value="statement">Statement</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* Tab 3: Conditions */}
                {activeTab === 3 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Min Purchase (₹)
                      </label>
                      <input
                        type="number"
                        className="w-full border rounded-lg p-2"
                        value={dealFormV2.conditions.min_purchase}
                        onChange={(e) =>
                          setDealFormV2({
                            ...dealFormV2,
                            conditions: {
                              ...dealFormV2.conditions,
                              min_purchase: parseInt(e.target.value) || 0,
                            },
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Max Purchase (₹)
                      </label>
                      <input
                        type="number"
                        className="w-full border rounded-lg p-2"
                        value={dealFormV2.conditions.max_purchase || ""}
                        onChange={(e) =>
                          setDealFormV2({
                            ...dealFormV2,
                            conditions: {
                              ...dealFormV2.conditions,
                              max_purchase: parseInt(e.target.value) || null,
                            },
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Promo Code Type
                      </label>
                      <select
                        className="w-full border rounded-lg p-2"
                        value={dealFormV2.conditions.code.type}
                        onChange={(e) =>
                          setDealFormV2({
                            ...dealFormV2,
                            conditions: {
                              ...dealFormV2.conditions,
                              code: {
                                ...dealFormV2.conditions.code,
                                type: e.target.value,
                              },
                            },
                          })
                        }
                      >
                        <option value="none">No Code</option>
                        <option value="static">Static Code</option>
                        <option value="dynamic">Dynamic Code</option>
                      </select>
                    </div>

                    {dealFormV2.conditions.code.type === "static" && (
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Promo Code
                        </label>
                        <input
                          type="text"
                          className="w-full border rounded-lg p-2"
                          placeholder="e.g., SAVE10"
                          value={dealFormV2.conditions.code.value || ""}
                          onChange={(e) =>
                            setDealFormV2({
                              ...dealFormV2,
                              conditions: {
                                ...dealFormV2.conditions,
                                code: {
                                  ...dealFormV2.conditions.code,
                                  value: e.target.value,
                                },
                              },
                            })
                          }
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Uses per User
                      </label>
                      <input
                        type="number"
                        className="w-full border rounded-lg p-2"
                        placeholder="Leave empty for unlimited"
                        value={dealFormV2.conditions.limit.per_user || ""}
                        onChange={(e) =>
                          setDealFormV2({
                            ...dealFormV2,
                            conditions: {
                              ...dealFormV2.conditions,
                              limit: {
                                ...dealFormV2.conditions.limit,
                                per_user: parseInt(e.target.value) || null,
                              },
                            },
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Usage Reset
                      </label>
                      <select
                        className="w-full border rounded-lg p-2"
                        value={dealFormV2.conditions.limit.reset}
                        onChange={(e) =>
                          setDealFormV2({
                            ...dealFormV2,
                            conditions: {
                              ...dealFormV2.conditions,
                              limit: {
                                ...dealFormV2.conditions.limit,
                                reset: e.target.value,
                              },
                            },
                          })
                        }
                      >
                        <option value="none">Never</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Tab 4: Schedule */}
                {activeTab === 4 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Start Date *
                      </label>
                      <input
                        type="datetime-local"
                        className="w-full border rounded-lg p-2"
                        value={dealFormV2.schedule.start}
                        onChange={(e) =>
                          setDealFormV2({
                            ...dealFormV2,
                            schedule: {
                              ...dealFormV2.schedule,
                              start: e.target.value,
                            },
                          })
                        }
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        End Date *
                      </label>
                      <input
                        type="datetime-local"
                        className="w-full border rounded-lg p-2"
                        value={dealFormV2.schedule.end}
                        onChange={(e) =>
                          setDealFormV2({
                            ...dealFormV2,
                            schedule: {
                              ...dealFormV2.schedule,
                              end: e.target.value,
                            },
                          })
                        }
                        required
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">
                        Valid Days
                      </label>
                      <div className="flex gap-3 flex-wrap">
                        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                          (day, idx) => (
                            <label
                              key={day}
                              className="flex items-center gap-1"
                            >
                              <input
                                type="checkbox"
                                checked={dealFormV2.schedule.valid_days.includes(
                                  idx
                                )}
                                onChange={(e) => {
                                  const days = e.target.checked
                                    ? [...dealFormV2.schedule.valid_days, idx]
                                    : dealFormV2.schedule.valid_days.filter(
                                        (d) => d !== idx
                                      );
                                  setDealFormV2({
                                    ...dealFormV2,
                                    schedule: {
                                      ...dealFormV2.schedule,
                                      valid_days: days,
                                    },
                                  });
                                }}
                              />
                              {day}
                            </label>
                          )
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Timezone
                      </label>
                      <select
                        className="w-full border rounded-lg p-2"
                        value={dealFormV2.schedule.timezone}
                        onChange={(e) =>
                          setDealFormV2({
                            ...dealFormV2,
                            schedule: {
                              ...dealFormV2.schedule,
                              timezone: e.target.value,
                            },
                          })
                        }
                      >
                        <option value="Asia/Kolkata">Asia/Kolkata</option>
                        <option value="UTC">UTC</option>
                        <option value="America/New_York">
                          America/New_York
                        </option>
                      </select>
                    </div>
                  </div>
                )}
                {activeTab === 5 && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Optional Card Add-on
                    </h3>
                    <p className="text-sm text-gray-500">
                      You can add one stackable card offer directly to this
                      deal.
                    </p>

                    {dealFormV2.addons && dealFormV2.addons.length > 0 ? (
                      // If an add-on exists, show its details
                      <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg">
                        {dealFormV2.addons.map((addon, index) => (
                          <div key={index}>
                            <div className="flex justify-between items-center mb-4">
                              <h4 className="font-semibold text-blue-800">
                                {addon.title || "Card Add-on"}
                              </h4>
                              <button
                                type="button"
                                onClick={() => {
                                  setDealFormV2({ ...dealFormV2, addons: [] });
                                }}
                                className="text-red-500 hover:text-red-700"
                              >
                                Remove
                              </button>
                            </div>

                            {/* Form to edit the existing add-on */}
                            <AddonDealForm
                              addon={addon}
                              index={index}
                              dealFormV2={dealFormV2}
                              setDealFormV2={setDealFormV2}
                              banks={banks}
                              cards={cards}
                              isLoadingTargets={isLoadingTargets}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      // Button to add a new add-on
                      <button
                        type="button"
                        onClick={() => {
                          const newAddon = {
                            addon_id: `addon_${new Date().getTime()}`,
                            title: "Extra Card Offer",
                            type: "cashback",
                            priority: 110,
                            benefit: {
                              kind: "cashback",
                              calc: {
                                value_type: "percent",
                                value: 0,
                                max: null,
                              },
                            },
                            eligible_cards: {
                              bank_ids: [],
                              card_ids: [],
                              card_networks: [],
                            },
                            conditions: { min_purchase: 0 },
                          };
                          setDealFormV2({ ...dealFormV2, addons: [newAddon] });
                        }}
                        className="w-full text-center py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-500"
                      >
                        + Add a Card Offer
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Footer Buttons */}
              <div className="flex justify-between items-center border-t pt-4 mt-6">
                <div className="flex gap-2">
                  {activeTab > 0 && (
                    <button
                      type="button"
                      onClick={() => setActiveTab(activeTab - 1)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-100"
                    >
                      Previous
                    </button>
                  )}
                  {activeTab < TABS.length - 1 && (
                    <button
                      type="button"
                      onClick={() => setActiveTab(activeTab + 1)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Next
                    </button>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className={`px-4 py-2 rounded-lg text-white ${
                      isSaving
                        ? "bg-blue-300 cursor-not-allowed"
                        : "bg-black hover:bg-gray-800"
                    }`}
                  >
                    {isSaving
                      ? "Saving..."
                      : editingDeal
                      ? "Update Deal"
                      : "Create Deal"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 transform transition-all">
            {/* Header */}
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <FaTrash className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-gray-900">
                  Delete Deal
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Are you sure you want to delete this deal? This action cannot
                  be undone.
                </p>
              </div>
            </div>

            {/* Deal Info */}
            {deleteModal.deal && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="space-y-2">
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase">
                      Deal Title
                    </span>
                    <p className="text-sm font-semibold text-gray-900">
                      {deleteModal.deal.title || deleteModal.deal.deal_name}
                    </p>
                  </div>

                  {deleteModal.deal.description && (
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase">
                        Description
                      </span>
                      <p className="text-sm text-gray-700">
                        {deleteModal.deal.description}
                      </p>
                    </div>
                  )}

                  <div className="flex gap-4 pt-2">
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase">
                        Type
                      </span>
                      <p className="text-sm text-gray-700">
                        {deleteModal.deal.type || deleteModal.deal.deal_type}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase">
                        Status
                      </span>
                      <p className="text-sm text-gray-700">
                        {deleteModal.deal.computed_status ||
                          deleteModal.deal.status ||
                          "Unknown"}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase">
                        Priority
                      </span>
                      <p className="text-sm text-gray-700">
                        {deleteModal.deal.priority || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Warning Message */}
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">
                    This will permanently delete the deal and cannot be
                    recovered.
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal({ isOpen: false, deal: null })}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  <>
                    <FaTrash size={14} />
                    Delete Deal
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Toast */}
      {toast.message && (
        <div
          className={`fixed top-5 right-5 px-4 py-2 rounded shadow text-white ${
            toast.type === "error" ? "bg-red-500" : "bg-green-600"
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
